import React, { useEffect, useId, useMemo, useRef } from 'react';
import { useLatest } from 'ahooks';
import type { LexicalEditor } from 'lexical';
import {
    COMMAND_PRIORITY_EDITOR,
    COMMAND_PRIORITY_HIGH,
    COMMAND_PRIORITY_LOW,
    KEY_DOWN_COMMAND,
    KEY_ENTER_COMMAND,
    PASTE_COMMAND,
    SELECTION_CHANGE_COMMAND,
    $createTextNode,
    $getRoot,
    $getSelection,
    $isRangeSelection,
} from 'lexical';
import { registerDragonSupport } from '@lexical/dragon';
import { registerRichText } from '@lexical/rich-text';
import { mergeRegister } from '@lexical/utils';
import { LexicalComposer } from '@lexical/react/LexicalComposer';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { ContentEditable } from '@lexical/react/LexicalContentEditable';
import { HistoryPlugin } from '@lexical/react/LexicalHistoryPlugin';
import LexicalErrorBoundary from '@lexical/react/LexicalErrorBoundary';
import { RichTextPlugin } from '@lexical/react/LexicalRichTextPlugin';
import { cn } from '@rocco/utils/cn';
import { FormControl } from '@rocco/components/form';
import { useUserLoggedIn } from '~/modules/user';
import type { Participant } from '../../types';
import { useConversation } from '../../context';
import { getMentionToken, type MentionLookup } from '../../utils/mentions';
import {
    ChatMentionNode,
    getChatEditorSnapshot,
    replaceActiveMentionQuery,
    setChatEditorStateFromMessage,
} from '../../utils/chat-composer-lexical';

function assignRef<T>(ref: React.Ref<T> | undefined, value: T) {
    if (typeof ref === 'function') {
        ref(value);
        return;
    }
    if (ref) {
        (ref as React.MutableRefObject<T>).current = value;
    }
}

export interface ChatInputComposerApi {
    /**
     * Focus the editor. Set the selection to the end of the editor if not
     * already focused.
     */
    focus: () => void;
    /**
     * Insert the selected participant into the active visible `@query`.
     *
     * This stays imperative on purpose so `ChatInput` can keep ownership of the
     * dropdown state while the composer owns the Lexical selection transaction.
     */
    insertMention: (participant: Participant) => void;
}

interface ChatInputComposerSelectionState {
    text: string;
    selectionStart: number | null;
}

interface ChatInputComposerProps extends Omit<
    React.ComponentPropsWithoutRef<'div'>,
    'onChange' | 'onInput' | 'onKeyUp'
> {
    message: string;
    // @types/react 18.3 dropped `placeholder` from the base div HTMLAttributes.
    placeholder?: string;
    composerApiRef?: React.Ref<ChatInputComposerApi | null>;
    isDisabled?: boolean;
    onChange?: (text: string, selectionStart: number | null) => void;
    onSelectionChange?: (state: ChatInputComposerSelectionState) => void;
    onKeyUp?: (
        e: React.KeyboardEvent<HTMLDivElement>,
        text: string,
        selectionStart: number | null,
    ) => void;
}

/**
 * The composer (input) for the chat. This should be inside the Rocco `Form` component.
 * @see {@link ChatInput}
 */
export const ChatInputComposer = React.forwardRef<
    HTMLDivElement,
    ChatInputComposerProps
>(function ChatInputComposer(
    {
        message,
        composerApiRef,
        placeholder,
        isDisabled = false,
        onChange,
        onClick,
        onKeyDown,
        onKeyUp,
        onPaste,
        onSelectionChange,
        ...props
    },
    ref,
) {
    const composerId = useId();
    const { mentionLookup } = useConversation();
    const { user, isLoggedIn } = useUserLoggedIn();
    const currentUserID =
        isLoggedIn && user.__typename === 'Me' ? user.ID : null;
    const initialConfig = useMemo(
        () => ({
            namespace: `chat-input-${composerId}`,
            onError(error: Error) {
                throw error;
            },
            nodes: [ChatMentionNode],
        }),
        [composerId],
    );

    return (
        <div className="relative">
            {!message && !!placeholder && (
                <span className="pointer-events-none absolute top-[8px] left-[12px] z-1 text-[16px] leading-tight text-gray-500 md:text-[14px] md:leading-tight">
                    {placeholder}
                </span>
            )}
            <LexicalComposer initialConfig={initialConfig}>
                <ChatComposerPlugins
                    editorRef={ref}
                    composerApiRef={composerApiRef}
                    currentUserID={currentUserID}
                    isDisabled={isDisabled}
                    mentionLookup={mentionLookup}
                    message={message}
                    onChange={onChange}
                    onKeyDown={onKeyDown}
                    onSelectionChange={onSelectionChange}
                />
                <RichTextPlugin
                    contentEditable={
                        <FormControl>
                            <ContentEditable
                                className={cn(
                                    'h-[80px] overflow-y-auto rounded-[8px] bg-[#f2f2f2] px-[12px] py-[8px] text-[16px] leading-tight wrap-break-word whitespace-pre-wrap text-black outline-hidden md:text-[14px]',
                                    isDisabled &&
                                        'cursor-not-allowed opacity-50',
                                )}
                                role="textbox"
                                spellCheck="false"
                                aria-label="Chat message input"
                                aria-placeholder={placeholder}
                                aria-multiline="true"
                                aria-autocomplete="list"
                                aria-disabled={isDisabled}
                                onClick={onClick}
                                {...props}
                            />
                        </FormControl>
                    }
                    placeholder={null}
                    ErrorBoundary={LexicalErrorBoundary}
                />
                <HistoryPlugin />
                <ChatComposerDomEventPlugin
                    onPaste={onPaste}
                    onKeyUp={onKeyUp}
                    onSelectionChange={onSelectionChange}
                />
            </LexicalComposer>
        </div>
    );
});

function ChatComposerPlugins({
    editorRef,
    composerApiRef,
    message,
    mentionLookup,
    currentUserID,
    isDisabled,
    onChange,
    onKeyDown,
    onSelectionChange,
}: {
    editorRef: React.Ref<HTMLDivElement | null>;
    composerApiRef?: React.Ref<ChatInputComposerApi | null>;
    message: string;
    mentionLookup: MentionLookup;
    currentUserID: number | null;
    isDisabled: boolean;
    onChange?: (text: string, selectionStart: number | null) => void;
    onKeyDown?: React.KeyboardEventHandler<HTMLDivElement>;
    onSelectionChange?: (state: ChatInputComposerSelectionState) => void;
}) {
    const [editor] = useLexicalComposerContext();
    const currentUserIDRef = useLatest(currentUserID);
    const onChangeRef = useLatest(onChange);
    const onKeyDownRef = useLatest(onKeyDown);
    const onSelectionChangeRef = useLatest(onSelectionChange);
    const previousMessageRef = useRef<string | null>(null);

    useEffect(() => {
        return mergeRegister(
            registerRichText(editor),
            registerDragonSupport(editor),
            // Consume keydown inside the editor so page-level shortcuts do not
            // fire while typing in chat. `ChatInput` still receives the event
            // through this callback and decides whether to prevent default.
            editor.registerCommand(
                KEY_DOWN_COMMAND,
                (event) => {
                    event.stopPropagation();
                    onKeyDownRef.current?.(
                        event as unknown as React.KeyboardEvent<HTMLDivElement>,
                    );
                    return event.defaultPrevented;
                },
                COMMAND_PRIORITY_HIGH,
            ),
            // Mentions are editable text nodes until the user changes the chip
            // contents. At that point we intentionally downgrade them to plain
            // text so the mention token mapping is removed.
            editor.registerNodeTransform(ChatMentionNode, (node) => {
                if (node.getTextContent() === node.getDisplayText()) {
                    return;
                }
                node.replace($createTextNode(node.getTextContent()));
            }),
            editor.registerCommand(
                PASTE_COMMAND,
                (event) => {
                    if (event.defaultPrevented) {
                        return true;
                    }

                    const selection = $getSelection();
                    if (
                        !$isRangeSelection(selection) ||
                        event instanceof InputEvent ||
                        event instanceof KeyboardEvent
                    ) {
                        return false;
                    }

                    const pastedText =
                        event.clipboardData?.getData('text/plain') ?? '';
                    event.preventDefault();
                    selection.insertRawText(pastedText);
                    return true;
                },
                COMMAND_PRIORITY_EDITOR,
            ),
            // Plain Enter is owned by `ChatInput` for "send message". Lexical
            // only handles modified Enter here so multiline insertion still
            // works without reviving DOM-based contentEditable helpers.
            editor.registerCommand(
                KEY_ENTER_COMMAND,
                (event) => {
                    if (event?.defaultPrevented) {
                        return true;
                    }
                    if (!event?.ctrlKey && !event?.altKey && !event?.shiftKey) {
                        return false;
                    }

                    event.preventDefault();
                    const selection = $getSelection();
                    if ($isRangeSelection(selection)) {
                        selection.insertRawText('\n');
                    }
                    return true;
                },
                COMMAND_PRIORITY_EDITOR,
            ),
            editor.registerCommand(
                SELECTION_CHANGE_COMMAND,
                () => {
                    emitSelectionChange({
                        editor,
                        onSelectionChange: onSelectionChangeRef.current,
                    });
                    return false;
                },
                COMMAND_PRIORITY_LOW,
            ),
            editor.registerUpdateListener(({ editorState, tags }) => {
                const snapshot = getChatEditorSnapshot(editorState);

                onSelectionChangeRef.current?.({
                    text: snapshot.visibleText,
                    selectionStart: snapshot.visibleSelectionStart,
                });

                if (tags.has('chat-sync')) {
                    previousMessageRef.current = snapshot.message;
                    return;
                }

                if (snapshot.message === previousMessageRef.current) {
                    return;
                }

                previousMessageRef.current = snapshot.message;
                onChangeRef.current?.(
                    snapshot.message,
                    snapshot.tokenSelectionStart,
                );
            }),
        );
    }, [editor, onChangeRef, onKeyDownRef, onSelectionChangeRef]);

    // Disable the editor from the `isDisabled` prop.
    useEffect(() => {
        editor.setEditable(!isDisabled);
    }, [editor, isDisabled]);

    // Set the editor state from the message.
    useEffect(() => {
        const snapshot = getChatEditorSnapshot(editor.getEditorState());
        if (snapshot.message === message) {
            previousMessageRef.current = message;
            return;
        }

        previousMessageRef.current = message;
        setChatEditorStateFromMessage({
            editor,
            message,
            mentionLookup,
            currentUserID,
        });
    }, [currentUserID, editor, mentionLookup, message]);

    // Assign the ref to the editor element.
    useEffect(() => {
        if (editorRef) {
            assignRef(editorRef, editor.getRootElement());
        }
    }, [editorRef, editor]);

    // Assign the composer API ref.
    useEffect(() => {
        if (!composerApiRef) {
            return;
        }

        assignRef(composerApiRef, {
            focus() {
                // root.selectEnd() may shift the page, so this is a workaround
                // to prevent the page from scrolling with this version of Lexical.
                setTimeout(() => {
                    editor.update(() => {
                        const root = $getRoot();
                        const selection = $getSelection();
                        const isSelectionPreserved =
                            $isRangeSelection(selection) &&
                            selection.anchor.offset > 0;
                        // Set selection to the end if the selection was lost.
                        if (!isSelectionPreserved) {
                            root.selectEnd();
                        }
                    });
                }, 150);
                const rootElement = editor.getRootElement();
                rootElement?.focus({ preventScroll: true });
            },
            insertMention(participant) {
                const identity = participant.Identity;

                replaceActiveMentionQuery({
                    editor,
                    token:
                        identity.__typename === 'Professional'
                            ? getMentionToken('P', identity.ID)
                            : getMentionToken('U', identity.ID),
                    displayName:
                        identity.__typename === 'Professional'
                            ? identity.Title
                            : identity.Name,
                    isSelfMention:
                        identity.__typename !== 'Professional' &&
                        currentUserIDRef.current !== null &&
                        identity.ID === currentUserIDRef.current,
                });

                editor.focus();
                emitSelectionChange({
                    editor,
                    onSelectionChange: onSelectionChangeRef.current,
                });
            },
        });

        return () => {
            assignRef(composerApiRef, null);
        };
    }, [composerApiRef, currentUserIDRef, editor, onSelectionChangeRef]);

    return null;
}

/**
 * `keyup` is still handled via the DOM root because chat only needs it for
 * selection/context refresh after Lexical has already processed the key.
 * Keeping this separate from the `KEY_DOWN_COMMAND` path avoids double handling
 * for navigation and submit-related keys.
 */
function ChatComposerDomEventPlugin({
    onPaste,
    onKeyUp,
    onSelectionChange,
}: {
    onPaste?: React.ClipboardEventHandler<HTMLDivElement>;
    onKeyUp?: (
        e: React.KeyboardEvent<HTMLDivElement>,
        text: string,
        selectionStart: number | null,
    ) => void;
    onSelectionChange?: (state: ChatInputComposerSelectionState) => void;
}) {
    const [editor] = useLexicalComposerContext();
    const onPasteRef = useLatest(onPaste);
    const onKeyUpRef = useLatest(onKeyUp);
    const onSelectionChangeRef = useLatest(onSelectionChange);
    const keyUpHandlerRef = useRef<(event: KeyboardEvent) => void>(undefined);
    const pasteHandlerRef = useRef<(event: ClipboardEvent) => void>(undefined);

    useEffect(() => {
        keyUpHandlerRef.current = (event: KeyboardEvent) => {
            event.stopPropagation();
            const snapshot = getChatEditorSnapshot(editor.getEditorState());

            onSelectionChangeRef.current?.({
                text: snapshot.visibleText,
                selectionStart: snapshot.visibleSelectionStart,
            });
            onKeyUpRef.current?.(
                event as unknown as React.KeyboardEvent<HTMLDivElement>,
                snapshot.visibleText,
                snapshot.visibleSelectionStart,
            );
        };
        pasteHandlerRef.current = (event: ClipboardEvent) => {
            if (!hasClipboardFiles(event.clipboardData)) {
                return;
            }
            onPasteRef.current?.(
                event as unknown as React.ClipboardEvent<HTMLDivElement>,
            );
        };

        return editor.registerRootListener((rootElement, prevRootElement) => {
            const keyUpHandler = keyUpHandlerRef.current;
            const pasteHandler = pasteHandlerRef.current;
            if (prevRootElement && keyUpHandler) {
                prevRootElement.removeEventListener('keyup', keyUpHandler);
            }
            if (prevRootElement && pasteHandler) {
                prevRootElement.removeEventListener(
                    'paste',
                    pasteHandler,
                    true,
                );
            }
            if (!rootElement || !keyUpHandler || !pasteHandler) {
                return;
            }

            rootElement.addEventListener('keyup', keyUpHandler);
            rootElement.addEventListener('paste', pasteHandler, true);
        });
    }, [editor, onKeyUpRef, onPasteRef, onSelectionChangeRef]);

    return null;
}

/**
 * Push the current visible-text caret state back to `ChatInput`.
 *
 * The parent mention dropdown logic works from the visible string, not the
 * tokenised message string, so this helper is the main bridge for that view.
 */
function emitSelectionChange({
    editor,
    onSelectionChange,
}: {
    editor: LexicalEditor;
    onSelectionChange?: (state: ChatInputComposerSelectionState) => void;
}) {
    if (!onSelectionChange) {
        return;
    }

    const snapshot = getChatEditorSnapshot(editor.getEditorState());
    onSelectionChange({
        text: snapshot.visibleText,
        selectionStart: snapshot.visibleSelectionStart,
    });
}

function hasClipboardFiles(clipboardData: DataTransfer | null): boolean {
    if (!clipboardData) {
        return false;
    }

    return (
        clipboardData.files.length > 0 ||
        Array.from(clipboardData.items).some((item) => item.kind === 'file')
    );
}
